import { z } from "zod";

const TOTAL_POSITIONS_MIN = 1;
const TOTAL_POSITIONS_MAX = 999;

const isEmpty = (val) => val === "" || val === null || val === undefined;

export const positionConfigurationSchema = (intl, maxPositions) => {
  return z
    .object({
      totalPositions: z
        .string()
        .refine((val) => !isEmpty(val), {
          message: intl.formatMessage({ id: "validation.required_field" }),
        })
        .refine(
          (val) => {
            const num = parseInt(val, 10);
            return (
              !Number.isNaN(num) &&
              num >= TOTAL_POSITIONS_MIN &&
              num <= TOTAL_POSITIONS_MAX
            );
          },
          {
            message: intl.formatMessage({
              id: "totem_position.invalid_total_positions",
            }),
          }
        ),

      ranges: z
        .array(
          z.object({
            id: z.number().optional(),
            areaId: z
              .object({
                value: z.number(),
                label: z.string(),
              })
              .nullable()
              .refine((val) => val !== null, {
                message: intl.formatMessage({
                  id: "validation.required_field",
                }),
              }),

            positionStart: z
              .union([z.string(), z.number()])
              .refine((val) => !isEmpty(val), {
                message: intl.formatMessage({
                  id: "validation.required_field",
                }),
              })
              .refine(
                (val) => {
                  const num = typeof val === "number" ? val : parseInt(val, 10);
                  return !Number.isNaN(num) && num >= 1;
                },
                {
                  message: intl.formatMessage({
                    id: "totem_position.position_start_required",
                  }),
                }
              ),

            positionEnd: z
              .union([z.string(), z.number()])
              .refine((val) => !isEmpty(val), {
                message: intl.formatMessage({
                  id: "validation.required_field",
                }),
              })
              .refine(
                (val) => {
                  const num = typeof val === "number" ? val : parseInt(val, 10);
                  return !Number.isNaN(num) && num >= 1;
                },
                {
                  message: intl.formatMessage({
                    id: "totem_position.position_end_required",
                  }),
                }
              ),

            canEdit: z.boolean().optional().default(true),
            canDelete: z.boolean().optional().default(true),
            hasActiveSessions: z.boolean().optional(),
            hasPrintedCredentials: z.boolean().optional(),
          })
        )
        .min(1, {
          message: intl.formatMessage({
            id: "totem_position.at_least_one_range",
          }),
        })
        .superRefine((ranges, ctx) => {
          ranges.forEach((range, index) => {
            const hasStart = !isEmpty(range.positionStart);
            const hasEnd = !isEmpty(range.positionEnd);
            if (!hasStart || !hasEnd) return;

            const start =
              typeof range.positionStart === "number"
                ? range.positionStart
                : parseInt(range.positionStart, 10);
            const end =
              typeof range.positionEnd === "number"
                ? range.positionEnd
                : parseInt(range.positionEnd, 10);

            if (!Number.isNaN(start) && !Number.isNaN(end) && start > end) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "positionEnd"],
                message: intl.formatMessage({
                  id: "totem_position.end_must_be_greater_than_start",
                }),
              });
            }
          });
        }),
    })
    .superRefine((data, ctx) => {
      const areaIds = data.ranges
        .map((r) => r.areaId?.value)
        .filter((id) => id !== null && id !== undefined);
      const uniqueAreaIds = new Set(areaIds);

      if (areaIds.length !== uniqueAreaIds.size) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ranges"],
          message: intl.formatMessage({ id: "totem_position.duplicate_areas" }),
        });
      }
    });
};
